/**
 * Report-Definition für die Stakeholder-Liste.
 *
 * Übersetzt die geladene Stakeholder-Liste in ein {@link ReportDocument}. Spalten
 * spiegeln die Kern-Spalten der UI-Tabelle: Name, Rolle, Typ, Macht/Einfluss,
 * Interesse. Leere Zellen werden als '—' dargestellt.
 */
import {
  buildDocument,
  tableBlock,
  type ReportBlock,
  type ReportDocument,
} from '../../../lib/pdf/reportModel';

export interface StakeholderReportItem {
  name: string;
  role: string | null;
  type: 'group' | 'person' | 'extern';
  power: number;
  interest: number;
}

export interface StakeholdersReportInput {
  items: StakeholderReportItem[];
}

const TYPE_LABEL: Record<StakeholderReportItem['type'], string> = {
  group: 'Gruppe',
  person: 'Person',
  extern: 'Extern',
};

function fmtNum(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? '—' : String(n);
}

export function buildStakeholdersReport(input: StakeholdersReportInput): ReportDocument {
  const block: ReportBlock =
    input.items.length > 0
      ? tableBlock(
          [
            { header: 'Name', weight: 2.4 },
            { header: 'Rolle', weight: 1.8 },
            { header: 'Typ', weight: 1.2 },
            { header: 'Macht/Einfluss', weight: 1.2 },
            { header: 'Interesse', weight: 1.2 },
          ],
          input.items.map((s) => [
            s.name || '—',
            s.role || '—',
            TYPE_LABEL[s.type] ?? '—',
            fmtNum(s.power),
            fmtNum(s.interest),
          ]),
        )!
      : { kind: 'note', value: 'Noch keine Stakeholder erfasst.' };

  return buildDocument([{ title: 'Stakeholder', blocks: [block] }]);
}
